import { Descriptions, Grid, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../shared/permissions';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import SuperiorUserGroupList from './SuperiorUserGroupList';
import UserGroupActions from './UserGroupActions';
import UserGroupDealerList from './UserGroupDealerList';
import UserGroupUserList from './UserGroupUserList';

export type UserGroupInnerPageProps = { userGroup: UserGroupSpecsFragment };

const UserGroupInnerPage = ({ userGroup }: UserGroupInnerPageProps) => {
    const { t } = useTranslation(['userGroupDetails', 'common']);

    const navigate = useNavigate();
    const company = useCompany(true);
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: userGroup.versioning,
        timeZone: userGroup.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const [activeTab, setActiveTab] = useState('users');
    const { permissions: accountPermissions } = useAccount();

    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.manageUserGroups]) &&
          hasPermissions(accountPermissions, [permissionKind.updateUserGroup])
        : hasPermissions(accountPermissions, [permissionKind.updateUserGroup]);

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('userGroupDetails:content.displayName')}>
                {userGroup.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('userGroupDetails:content.company')}>
                {userGroup.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('userGroupDetails:content.parentUserGroupCount')}>
                {userGroup.superiorUserGroups.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('userGroupDetails:content.userCount')}>
                {userGroup.users.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('userGroupDetails:content.dealerCount')}>
                {userGroup.dealers.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('userGroupDetails:content.isParallel')}>
                {userGroup.isParallel ? t('common:options.yesNo.yes') : t('common:options.yesNo.no')}
            </Descriptions.Item>
            {userGroup.description && (
                <Descriptions.Item label={t('userGroupDetails:content.description')}>
                    {userGroup.description}
                </Descriptions.Item>
            )}
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={hasPermission && <UserGroupActions userGroup={userGroup} />}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <Tabs.TabPane key="users" tab={t('userGroupDetails:tabs.users.title')} />
                        <Tabs.TabPane key="parentUserGroups" tab={t('userGroupDetails:tabs.parentUserGroups.title')} />
                        <Tabs.TabPane key="dealers" tab={t('userGroupDetails:tabs.dealers.title')} />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/accesses/userGroups')}
            title={screens.md ? t('userGroupDetails:title', { name: userGroup.displayName }) : userGroup.displayName}
        >
            {(() => {
                switch (activeTab) {
                    case 'parentUserGroups':
                        return <SuperiorUserGroupList userGroup={userGroup} />;

                    case 'users':
                        return <UserGroupUserList userGroup={userGroup} />;

                    case 'dealers':
                        return <UserGroupDealerList userGroup={userGroup} />;

                    default:
                        return null;
                }
            })()}
        </ConsolePageWithHeader>
    );
};

export default UserGroupInnerPage;
